// Session store — the big stateful bucket equivalent to the vanilla codebase's
// global `S`. Everything that participates in saveSession() / restoreSession()
// lives here. Config + toolId are set once on load and effectively read-only.

import { create } from 'zustand';
import type {
  Asset,
  AssetType,
  Block,
  MasterClip,
  SessionState,
  STSAssetState,
  STSTake,
  ToolConfig,
  UploadVersion,
} from '@shared/types';
import { DEFAULT_ASSET_DURATION } from '@shared/constants';
import { applyPredefinedFreeVoiceFallback, buildAssetList } from '@/lib/assetList';

// Re-exported for the existing call sites; the implementation lives in lib.
export { applyPredefinedFreeVoiceFallback };

// MAX_TAKES_PER_BLOCK is exported from @shared/constants so the UI can read
// the same cap (Block.tsx renders "N / 5"). Re-imported below.

/** Per-asset music/SFX bed track. Populated on upload + on session
 *  hydration from IndexedDB (see `STORE_SFX_BEDS` in src/lib/indexeddb.ts,
 *  mirrored by useSessionPersistence). This slot stays out of
 *  `SessionState` / `sliceSession` because the playable `url` is an
 *  ephemeral blob: URL and the heavy `buffer` lives in its own IDB store.
 *  IDB is the source of truth across reloads. */
export interface SfxBed {
  /** Stable id for this bed (derived from `Date.now()` at upload time). */
  id: string;
  /** Original file name shown in the SfxTrack row. */
  name: string;
  /** Duration in seconds (from the decoded buffer or HTMLMediaElement). */
  duration: number;
  /** Object URL pointing at the source Blob — recreated on hydrate. */
  url: string;
  /** Decoded MP3/etc. bytes; used by `useTimelinePreview` to schedule
   *  playback through Web Audio (separate gain node so the bed can be
   *  ducked independently of the voice bus). */
  buffer: ArrayBuffer;
  /** MIME type of the source file — used to rebuild the blob: URL with
   *  the right type on hydrate. Absent on pre-persistence beds. */
  mimeType?: string;
  /** place-mode: clip start on the asset timeline (s). */
  offset?: number;
  /** window-mode: source window start (s) when the bed is longer than the
   *  asset. */
  sourceOffset?: number;
}

interface SessionSlice extends SessionState {
  toolId: string | null;
  config: ToolConfig | null;
  loaded: boolean;

  /** Per-asset music/SFX bed list — up to 2 tracks per asset, ordered from
   *  Track 1 (slot 0) → Track 2 (slot 1). Removing slot 0 promotes slot 1
   *  to slot 0, so there's never a populated slot 1 with an empty slot 0.
   *  Runtime only (see `SfxBed` doc). */
  sfxBeds: Record<number, SfxBed[]>;

  /** Per-asset busy flag — runtime only. Mirrors vanilla's
   *  `_sidebarSpinners` Set (legacy/index.html line 2708, used at lines
   *  3546 + 3585 to swap the asset's status icon for a spinner during an
   *  in-flight upload or delete). When true, AssetNav/AssetPillBar render
   *  a spinning icon instead of the regular pending/ready/uploaded glyph. */
  assetBusy: Record<number, boolean>;

  /** Runtime only — true for a few seconds after the talent dismissed the
   *  browser's "leave page?" dialog while assets were still un-uploaded.
   *  AssetNav pulses every not-yet-uploaded asset while it is set
   *  (useLeaveGuard, GitHub #1 point 2). */
  uploadReminder: boolean;
  /** Runtime only — number of versions uploaded in THIS browser session
   *  (bumped by `addVersion`, untouched by the restore path `setVersions`).
   *  Generation/index.tsx shows the first-upload popup when it goes 0 → 1;
   *  watching `versions` instead would misfire on a restored session. */
  sessionUploads: number;

  setLoaded(config: ToolConfig, toolId: string): void;
  setName(name: string): void;
  setSessionFolder(folder: string, shortId: string): void;
  setCurrentAssetIdx(idx: number): void;

  setBlocks(blocks: Block[]): void;
  setAssetBlocks(idx: number, blocks: Block[]): void;
  /** Functional update for the currently-active asset's blocks. Writes to
   *  both `blocks` (active view) and `assetBlocks[currentAssetIdx]` (persisted
   *  per-asset cache) so UI code doesn't have to keep them in sync. */
  updateActiveBlocks(updater: (prev: Block[]) => Block[]): void;
  /** Wie `updateActiveBlocks`, aber für einen FESTGEHALTENEN Asset-Index —
   *  für alles, was nach einem `await` schreibt (TTS-Response, Decode).
   *  Hat das Talent inzwischen das Asset gewechselt, landet die Änderung
   *  trotzdem am richtigen Asset; der `blocks`-Spiegel wird nur berührt,
   *  wenn `assetIdx` noch das aktive Asset ist (Audit M17). */
  updateAssetBlocks(assetIdx: number, updater: (prev: Block[]) => Block[]): void;

  /** Remove a take at `takeIdx` from the active asset's block. If the removed
   *  take was the favorite, favIdx is reset to -1; otherwise it's adjusted
   *  so it still points at the same take object. */
  removeTake(blockIdx: number, takeIdx: number): void;
  /** Set (or clear with -1) the favorite-take index on a block. */
  setBlockFavIdx(blockIdx: number, favIdx: number): void;
  /** Replace exactly one block at `blockIdx` with N new blocks (splice +
   *  insert). Used by Auto-Split. Writes to both `blocks` and
   *  `assetBlocks[currentAssetIdx]`, matching `updateActiveBlocks`. */
  replaceBlock(blockIdx: number, nextBlocks: Block[]): void;

  setStsAsset(idx: number, next: STSAssetState): void;
  /** Bump (and return) this asset's recording-session counter — the value to
   *  stamp onto every `STSTake.session` recorded in the next overlay open.
   *  Initializes the slot if missing. Persistent: each "+ New Recording
   *  Session" yields a fresh number for the lifetime of the session. */
  beginStsRecordingSession(assetIdx: number): number;
  /** Append a take to the STS asset at `assetIdx`. Initializes the slot if
   *  missing. Matches the new recording-booth data model (Phase 6 spec). */
  addSTSTake(assetIdx: number, take: STSTake): void;
  /** Upsert STSTake keyed nach (session, voiceId). Wenn ein Take für diese
   *  Voice in derselben Recording-Session schon existiert: ersetze in-place
   *  (preserving the slot identity). Sonst: append. Für Multi-Voice
   *  Re-Generate-Flows (#173 Phase 6). */
  upsertSTSTakeByVoice(assetIdx: number, take: STSTake): void;
  /** Remove a take by id. Cascades to all MasterClips whose `takeId` points
   *  at the removed take — master-clip references cannot outlive their source. */
  removeSTSTake(assetIdx: number, takeId: string): void;
  /** Append a master clip to the STS asset at `assetIdx`. Initializes the
   *  slot if missing. */
  addMasterClip(assetIdx: number, clip: MasterClip): void;
  /** Remove a master clip by id. No cascade — takes are source material. */
  removeMasterClip(assetIdx: number, clipId: string): void;
  /** Patch a master clip in-place. Used by trim-handle / drag callers
   *  (Task 6.3) — merged with `Partial<MasterClip>` fields. */
  updateMasterClip(
    assetIdx: number,
    clipId: string,
    patch: Partial<MasterClip>,
  ): void;
  /** Bulk apply new offsets to master clips after a drag-resolver pass.
   *  Single set() so React renders once for the whole reflow. */
  setMasterClipOffsets(assetIdx: number, offsets: Map<string, number>): void;

  upsertCustomAsset(asset: Asset): void;
  removeCustomAsset(assetId: string): void;

  setCustomDuration(idx: number, seconds: number): void;
  setCustomVideoEnabled(idx: number, enabled: boolean): void;
  /** Drop any `customDurations[idx]` entry that was auto-set from a now-
   *  missing local video upload. Called once after session hydration —
   *  blob URLs aren't persisted across reloads, so a 60s video that
   *  populated customDuration on first load leaves a stale 60s timeline
   *  boundary on reload with nothing to anchor it. Skips entries when
   *  any chip's right edge would land past the fallback boundary
   *  (asset.targetDuration → SFX bed → DEFAULT_ASSET_DURATION) — keeping
   *  the user's existing work safely inside the timeline. Admin
   *  videoPath assets are never reset since the video can re-stream. */
  cleanupOrphanedCustomDurations(): void;

  setFreeMode(idx: number, mode: AssetType): void;
  setFreeBlocks(idx: number, mode: 'tts' | 'sts', blocks: Block[]): void;
  setFreeActiveIdx(idx: number, activeIdx: number): void;

  setVersions(versions: Record<string, UploadVersion[]>): void;
  addVersion(assetId: string, version: UploadVersion): void;
  /** Eine Version aus dem lokalen Stand nehmen — nach `cancelUploadVersion`
   *  einer halben (pending) Version (Audit M15). */
  removeVersion(assetId: string, version: number): void;
  /** Update a single version's note in-place (optimistic UI for inline-edit).
   *  No-op when the asset/version isn't found. Mirrors vanilla
   *  `versionEditNote` (legacy/index.html:10155). */
  updateVersionNote(assetId: string, version: number, note: string | null): void;
  /** Set or clear (with `null`) the "final pick" version for an asset.
   *  Single-flag-per-asset invariant: assigning a version automatically
   *  clears `isFinal` on every other version of the same asset. Optimistic;
   *  callers handle the API request + revert on failure. */
  setVersionFinal(assetId: string, version: number | null): void;

  /** Append a new bed to an asset's slot list. No-op when both slots are
   *  full. Caller revokes the prior blob URL on replace; here we only
   *  manage the in-memory list. */
  addSfxBed(assetIdx: number, bed: SfxBed): void;
  /** Replace the bed at `trackIdx` (0 or 1). No-op if no bed exists at
   *  that index. */
  replaceSfxBed(assetIdx: number, trackIdx: number, bed: SfxBed): void;
  /** Remove the bed at `trackIdx`. Subsequent slots shift down so slot 1
   *  promotes to slot 0 if slot 0 is removed — invariant: a populated
   *  slot 1 cannot coexist with an empty slot 0. Caller revokes the
   *  removed bed's blob URL. */
  removeSfxBed(assetIdx: number, trackIdx: number): void;
  /** Drop every bed for an asset (e.g. asset deletion). Caller revokes
   *  blob URLs. */
  clearSfxBeds(assetIdx: number): void;
  /** Update a bed's placement (place-mode offset OR window-mode sourceOffset).
   *  No-op if the (assetIdx, trackIdx) slot is empty. */
  setBedPlacement(
    assetIdx: number,
    trackIdx: number,
    patch: { offset?: number; sourceOffset?: number },
  ): void;

  /** Mark/unmark an asset as having an in-flight async op (upload or
   *  delete). Callers MUST wrap the op in try/finally so the flag is
   *  always cleared on error. Mirrors vanilla `_sidebarSpinners.add` /
   *  `_sidebarSpinners.clear` (legacy/index.html lines 3729 + 3774/3801). */
  setAssetBusy(assetIdx: number, busy: boolean): void;
  setUploadReminder(on: boolean): void;

  reset(): void;
}

function initialSession(): SessionState {
  return {
    name: '',
    sessionFolder: '',
    shortId: '',
    currentAssetIdx: 0,
    blocks: [],
    assetBlocks: {},
    stsAssets: {},
    customAssets: [],
    customDurations: {},
    customVideoEnabled: {},
    freeMode: {},
    freeBlocks: {},
    freeActiveIdx: {},
    versions: {},
  };
}

export const useSessionStore = create<SessionSlice>((set) => ({
  toolId: null,
  config: null,
  loaded: false,
  // Runtime-only — not part of SessionState/sliceSession. Hydrated from
  // IndexedDB by the SfxTrack mount effect.
  sfxBeds: {},
  // Runtime-only — see `_sidebarSpinners` in legacy/index.html:2708. Cleared
  // in try/finally by every async caller (per-asset upload + custom delete).
  assetBusy: {},
  uploadReminder: false,
  sessionUploads: 0,
  ...initialSession(),

  setLoaded: (config, toolId) => set({ config, toolId, loaded: true }),
  setName: (name) => set({ name }),
  setSessionFolder: (sessionFolder, shortId) => set({ sessionFolder, shortId }),
  setCurrentAssetIdx: (currentAssetIdx) => set({ currentAssetIdx }),

  setBlocks: (blocks) => set({ blocks }),
  setAssetBlocks: (idx, blocks) =>
    set((s) => ({ assetBlocks: { ...s.assetBlocks, [idx]: blocks } })),
  updateActiveBlocks: (updater) =>
    set((s) => {
      const prev = s.assetBlocks[s.currentAssetIdx] ?? s.blocks;
      const next = updater(prev);
      return {
        blocks: next,
        assetBlocks: { ...s.assetBlocks, [s.currentAssetIdx]: next },
      };
    }),

  updateAssetBlocks: (assetIdx, updater) =>
    set((s) => {
      const prev = s.assetBlocks[assetIdx] ?? (assetIdx === s.currentAssetIdx ? s.blocks : []);
      const next = updater(prev);
      if (next === prev) return {};
      return {
        assetBlocks: { ...s.assetBlocks, [assetIdx]: next },
        ...(assetIdx === s.currentAssetIdx ? { blocks: next } : {}),
      };
    }),

  removeTake: (blockIdx, takeIdx) =>
    set((s) => {
      const prev = s.assetBlocks[s.currentAssetIdx] ?? s.blocks;
      const target = prev[blockIdx];
      if (!target || !target.takes[takeIdx]) return {};
      const takes = target.takes.slice();
      takes.splice(takeIdx, 1);
      let favIdx = target.favIdx;
      if (favIdx === takeIdx) favIdx = -1;
      else if (favIdx > takeIdx) favIdx--;
      const nextBlock: Block = { ...target, takes, favIdx };
      const nextBlocks = prev.slice();
      nextBlocks[blockIdx] = nextBlock;
      return {
        blocks: nextBlocks,
        assetBlocks: { ...s.assetBlocks, [s.currentAssetIdx]: nextBlocks },
      };
    }),

  setBlockFavIdx: (blockIdx, favIdx) =>
    set((s) => {
      const prev = s.assetBlocks[s.currentAssetIdx] ?? s.blocks;
      const target = prev[blockIdx];
      if (!target) return {};
      const nextBlock: Block = { ...target, favIdx };
      const nextBlocks = prev.slice();
      nextBlocks[blockIdx] = nextBlock;
      return {
        blocks: nextBlocks,
        assetBlocks: { ...s.assetBlocks, [s.currentAssetIdx]: nextBlocks },
      };
    }),

  replaceBlock: (blockIdx, nextBlocksForSlot) =>
    set((s) => {
      const prev = s.assetBlocks[s.currentAssetIdx] ?? s.blocks;
      if (!prev[blockIdx]) return {};
      const nextBlocks = [
        ...prev.slice(0, blockIdx),
        ...nextBlocksForSlot,
        ...prev.slice(blockIdx + 1),
      ];
      return {
        blocks: nextBlocks,
        assetBlocks: { ...s.assetBlocks, [s.currentAssetIdx]: nextBlocks },
      };
    }),

  setStsAsset: (idx, next) =>
    set((s) => ({ stsAssets: { ...s.stsAssets, [idx]: next } })),

  beginStsRecordingSession: (assetIdx): number => {
    // Read-modify-write inside a single `set` updater so this is race-free
    // even if React batches multiple opens. The new counter value is captured
    // in the closure and returned to the caller via the outer `let` below —
    // Zustand's `set` is synchronous so this is safe.
    let claimed = 1;
    set((s) => {
      const cur = s.stsAssets[assetIdx] ?? { takes: [], master: [] };
      claimed = (cur.sessionCounter ?? 0) + 1;
      const next: STSAssetState = { ...cur, sessionCounter: claimed };
      return { stsAssets: { ...s.stsAssets, [assetIdx]: next } };
    });
    return claimed;
  },

  addSTSTake: (assetIdx, take) =>
    set((s) => {
      const cur = s.stsAssets[assetIdx] ?? { takes: [], master: [] };
      const next: STSAssetState = { ...cur, takes: [...cur.takes, take] };
      return { stsAssets: { ...s.stsAssets, [assetIdx]: next } };
    }),

  upsertSTSTakeByVoice: (assetIdx, take) =>
    set((s) => {
      const cur = s.stsAssets[assetIdx] ?? { takes: [], master: [] };
      const existingIdx = cur.takes.findIndex(
        (t) => t.session === take.session && t.voiceId === take.voiceId,
      );
      const nextTakes = cur.takes.slice();
      if (existingIdx >= 0) {
        nextTakes[existingIdx] = take;
      } else {
        nextTakes.push(take);
      }
      const next: STSAssetState = { ...cur, takes: nextTakes };
      return { stsAssets: { ...s.stsAssets, [assetIdx]: next } };
    }),

  removeSTSTake: (assetIdx, takeId) =>
    set((s) => {
      const cur = s.stsAssets[assetIdx];
      if (!cur) return {};
      const next: STSAssetState = {
        takes: cur.takes.filter((t) => t.id !== takeId),
        // Cascade: drop master clips that reference the removed take.
        master: cur.master.filter((c) => c.takeId !== takeId),
      };
      return { stsAssets: { ...s.stsAssets, [assetIdx]: next } };
    }),

  addMasterClip: (assetIdx, clip) =>
    set((s) => {
      const cur = s.stsAssets[assetIdx] ?? { takes: [], master: [] };
      const next: STSAssetState = { ...cur, master: [...cur.master, clip] };
      return { stsAssets: { ...s.stsAssets, [assetIdx]: next } };
    }),

  removeMasterClip: (assetIdx, clipId) =>
    set((s) => {
      const cur = s.stsAssets[assetIdx];
      if (!cur) return {};
      const next: STSAssetState = {
        ...cur,
        master: cur.master.filter((c) => c.id !== clipId),
      };
      return { stsAssets: { ...s.stsAssets, [assetIdx]: next } };
    }),

  updateMasterClip: (assetIdx, clipId, patch) =>
    set((s) => {
      const cur = s.stsAssets[assetIdx];
      if (!cur) return {};
      const idx = cur.master.findIndex((c) => c.id === clipId);
      if (idx === -1) return {};
      const nextMaster = cur.master.slice();
      nextMaster[idx] = { ...nextMaster[idx], ...patch };
      const next: STSAssetState = { ...cur, master: nextMaster };
      return { stsAssets: { ...s.stsAssets, [assetIdx]: next } };
    }),

  setMasterClipOffsets: (assetIdx, offsets) =>
    set((s) => {
      const cur = s.stsAssets[assetIdx];
      if (!cur) return {};
      let changed = false;
      const nextMaster = cur.master.map((c) => {
        if (!offsets.has(c.id)) return c;
        const next = offsets.get(c.id)!;
        if (Math.abs(c.offset - next) < 0.001) return c;
        changed = true;
        return { ...c, offset: next };
      });
      if (!changed) return {};
      const next: STSAssetState = { ...cur, master: nextMaster };
      return { stsAssets: { ...s.stsAssets, [assetIdx]: next } };
    }),

  upsertCustomAsset: (asset) =>
    set((s) => {
      // Defensive clamp — title is capped at 50 chars in the UI via
      // `<input maxLength>`, but this guards every other path (paste,
      // programmatic upserts) so the constraint is enforced at the data
      // boundary too.
      const clamped: Asset =
        asset.title && asset.title.length > 50
          ? { ...asset, title: asset.title.slice(0, 50) }
          : asset;
      const existing = s.customAssets.findIndex((a) => a.id === clamped.id);
      if (existing === -1) return { customAssets: [...s.customAssets, clamped] };
      const next = s.customAssets.slice();
      next[existing] = clamped;
      return { customAssets: next };
    }),
  removeCustomAsset: (assetId) =>
    set((s) => ({ customAssets: s.customAssets.filter((a) => a.id !== assetId) })),

  setCustomDuration: (idx, seconds) =>
    set((s) => ({ customDurations: { ...s.customDurations, [idx]: seconds } })),
  setCustomVideoEnabled: (idx, enabled) =>
    set((s) => ({ customVideoEnabled: { ...s.customVideoEnabled, [idx]: enabled } })),

  cleanupOrphanedCustomDurations: () =>
    set((s) => {
      if (!s.config) return {};
      const merged = [...(s.config.assets ?? []), ...s.customAssets];
      const next = { ...s.customDurations };
      let changed = false;
      for (const idxStr of Object.keys(next)) {
        const idx = Number(idxStr);
        const asset = merged[idx];
        if (!asset) continue;
        // Admin videoPath: video can re-stream on next visit, so the
        // length anchored to it is still valid — keep customDuration.
        if (asset.videoPath) continue;
        // Boundary the timeline would revert to without the customDuration.
        // Use the longest bed track's duration so a long ambience layer
        // anchors the timeline even if track 1 is shorter.
        const beds = s.sfxBeds[idx] ?? [];
        const bedDur = beds.reduce((m, b) => Math.max(m, b.duration || 0), 0);
        const fallback =
          asset.targetDuration && asset.targetDuration > 0
            ? asset.targetDuration
            : bedDur > 0
              ? bedDur
              : DEFAULT_ASSET_DURATION;
        // If any chip's right edge would land past the fallback, the user
        // has work that depends on the longer timeline — keep it.
        const tts = s.assetBlocks[idx] ?? [];
        const ttsOverflow = tts.some((b) => {
          if (b.favIdx < 0) return false;
          const t = b.takes[b.favIdx];
          if (!t) return false;
          return (b.offset || 0) + (t.duration || 0) > fallback + 0.01;
        });
        if (ttsOverflow) continue;
        const sts = s.stsAssets[idx]?.master ?? [];
        const stsOverflow = sts.some(
          (c) => c.offset + (c.outPoint - c.inPoint) > fallback + 0.01,
        );
        if (stsOverflow) continue;
        const free = s.freeBlocks[idx];
        const freeOverflow =
          free &&
          (['tts', 'sts'] as const).some((mode) => {
            const arr = free[mode] ?? [];
            return arr.some((b) => {
              if (b.favIdx < 0) return false;
              const t = b.takes[b.favIdx];
              if (!t) return false;
              return (b.offset || 0) + (t.duration || 0) > fallback + 0.01;
            });
          });
        if (freeOverflow) continue;
        delete next[idx];
        changed = true;
      }
      if (!changed) return {};
      return { customDurations: next };
    }),

  setFreeMode: (idx, mode) =>
    set((s) => ({ freeMode: { ...s.freeMode, [idx]: mode } })),
  setFreeBlocks: (idx, mode, blocks) =>
    set((s) => {
      const forIdx = { ...(s.freeBlocks[idx] ?? { tts: [], sts: [] }), [mode]: blocks };
      return { freeBlocks: { ...s.freeBlocks, [idx]: forIdx } };
    }),
  setFreeActiveIdx: (idx, activeIdx) =>
    set((s) => ({ freeActiveIdx: { ...s.freeActiveIdx, [idx]: activeIdx } })),

  setVersions: (versions) => set({ versions }),
  addVersion: (assetId, version) =>
    set((s) => {
      const list = s.versions[assetId] ?? [];
      return {
        versions: { ...s.versions, [assetId]: [...list, version] },
        sessionUploads: s.sessionUploads + 1,
      };
    }),
  removeVersion: (assetId, version) =>
    set((s) => {
      const list = s.versions[assetId];
      if (!list) return {};
      const nextList = list.filter((v) => v.version !== version);
      if (nextList.length === list.length) return {};
      const versions = { ...s.versions };
      if (nextList.length === 0) delete versions[assetId];
      else versions[assetId] = nextList;
      return { versions };
    }),
  updateVersionNote: (assetId, version, note) =>
    set((s) => {
      const list = s.versions[assetId];
      if (!list) return {};
      const idx = list.findIndex((v) => v.version === version);
      if (idx === -1) return {};
      const nextList = list.slice();
      nextList[idx] = { ...nextList[idx], note };
      return { versions: { ...s.versions, [assetId]: nextList } };
    }),

  setVersionFinal: (assetId, version) =>
    set((s) => {
      const list = s.versions[assetId];
      if (!list) return {};
      // Single-flag-per-asset: clear on all, then set on the matching one
      // (or skip the set step entirely when `version` is null).
      const nextList = list.map((v) =>
        v.version === version
          ? { ...v, isFinal: true }
          : v.isFinal
          ? { ...v, isFinal: false }
          : v,
      );
      return { versions: { ...s.versions, [assetId]: nextList } };
    }),

  addSfxBed: (assetIdx, bed) =>
    set((s) => {
      const cur = s.sfxBeds[assetIdx] ?? [];
      if (cur.length >= 2) return {};
      return { sfxBeds: { ...s.sfxBeds, [assetIdx]: [...cur, bed] } };
    }),

  replaceSfxBed: (assetIdx, trackIdx, bed) =>
    set((s) => {
      const cur = s.sfxBeds[assetIdx] ?? [];
      if (trackIdx < 0 || trackIdx >= cur.length) return {};
      const next = cur.slice();
      next[trackIdx] = bed;
      return { sfxBeds: { ...s.sfxBeds, [assetIdx]: next } };
    }),

  removeSfxBed: (assetIdx, trackIdx) =>
    set((s) => {
      const cur = s.sfxBeds[assetIdx] ?? [];
      if (trackIdx < 0 || trackIdx >= cur.length) return {};
      const next = cur.filter((_, i) => i !== trackIdx);
      // Promote slot 1 → slot 0 implicitly via filter — the array now has
      // no gap. If both slots are removed, drop the asset key entirely.
      const nextMap = { ...s.sfxBeds };
      if (next.length === 0) delete nextMap[assetIdx];
      else nextMap[assetIdx] = next;
      return { sfxBeds: nextMap };
    }),

  clearSfxBeds: (assetIdx) =>
    set((s) => {
      if (!(assetIdx in s.sfxBeds)) return {};
      const next = { ...s.sfxBeds };
      delete next[assetIdx];
      return { sfxBeds: next };
    }),

  setBedPlacement: (assetIdx, trackIdx, patch) =>
    set((s) => {
      const cur = s.sfxBeds[assetIdx];
      if (!cur || !cur[trackIdx]) return {};
      const next = cur.map((b, i) => (i === trackIdx ? { ...b, ...patch } : b));
      return { sfxBeds: { ...s.sfxBeds, [assetIdx]: next } };
    }),

  setAssetBusy: (assetIdx, busy) =>
    set((s) => {
      // Drop the key entirely on `false` so `Object.keys(assetBusy).length`
      // is a reliable "anything in flight?" check, mirroring vanilla's
      // `_sidebarSpinners.has(idx)` semantics (legacy line 3546).
      if (!busy) {
        if (!(assetIdx in s.assetBusy)) return {};
        const next = { ...s.assetBusy };
        delete next[assetIdx];
        return { assetBusy: next };
      }
      return { assetBusy: { ...s.assetBusy, [assetIdx]: true } };
    }),

  setUploadReminder: (on) => set({ uploadReminder: on }),

  reset: () => set({ ...initialSession(), sfxBeds: {}, assetBusy: {}, uploadReminder: false, sessionUploads: 0 }),
}));

/** Store-bound wrapper around `buildAssetList` (src/lib/assetList.ts).
 *  Returns a fresh array per call — subscribe to the slices and memoise with
 *  `buildAssetList` in components instead of selecting through this. */
export function selectAllAssets(s: SessionSlice): Asset[] {
  // Migration runs at the boundary: api.ts hydrates ToolConfig assets,
  // useSessionPersistence rehydrates customAssets. selectAllAssets trusts
  // the boundary — no re-migration here per #173 review.
  return buildAssetList(s.config?.assets, s.config?.voices, s.customAssets);
}
